/**
 * High-precision game clock. Tracks delta time, elapsed time, FPS,
 * and frame count. Uses performance.now() for accuracy.
 *
 * Delta time is capped to prevent spiral-of-death when the tab
 * is backgrounded and then resumed.
 */
export class Clock {
  private _last = 0;
  private _elapsed = 0;
  private _frameCount = 0;
  private _fps = 0;
  private _fpsAccum = 0;
  private _fpsFrames = 0;
  private _hiddenAt = 0;
  /** Max delta in seconds. Prevents huge jumps after tab-switch. */
  private readonly _maxDt = 1 / 20; // 50ms cap

  get elapsed(): number { return this._elapsed; }
  get frameCount(): number { return this._frameCount; }
  get fps(): number { return this._fps; }

  /**
   * Call once per frame. Returns capped delta time in seconds.
   * First call returns 0 to establish baseline.
   */
  tick(now: number): number {
    if (this._last === 0) {
      this._last = now;
      return 0;
    }

    let dt = (now - this._last) / 1000;
    this._last = now;

    if (dt > this._maxDt) dt = this._maxDt;

    this._elapsed += dt;
    this._frameCount++;

    this._fpsFrames++;
    this._fpsAccum += dt;
    if (this._fpsAccum >= 1) {
      this._fps = this._fpsFrames / this._fpsAccum;
      this._fpsFrames = 0;
      this._fpsAccum = 0;
    }

    return dt;
  }

  /** Record timestamp when tab becomes hidden. */
  notifyHidden(): void {
    this._hiddenAt = performance.now();
  }

  /**
   * Call when tab becomes visible again. Returns gap in seconds.
   * Resets _last so the next tick() doesn't produce a huge dt.
   */
  notifyVisible(now: number): number {
    if (this._hiddenAt === 0) return 0;
    const gap = (now - this._hiddenAt) / 1000;
    this._hiddenAt = 0;
    this._last = now;
    return Math.max(0, gap);
  }
}
